import axios from 'axios';
import { Fragment, useEffect, useState } from 'react';
import { TypeAnimation } from 'react-type-animation';
import { Button, Spin, Tooltip } from 'antd';
import 'react-medium-image-zoom/dist/styles.css';

import { Layout, CtaForm, SignupForm } from '../../components';

import { openNotification } from '../../utils';

import fileService from '../../services/fileService';
import { useCopyToClipboard } from '../../hooks';
import { DeleteOutlined } from '@ant-design/icons';
import { ChecklistPdfDetail } from '../../components/Checklist/ChecklistPdfDetail';

import { frequencyOptions, payloadToExportPdf, downloadFile } from '../../utils/pdf';
import { AI_SERVICE_PROMPTS } from '../../constants/makergpt';
import { ChecklistsGenerationResponse } from '../../response_schemas/checklists';

const SelectFrequency = ({ onChange }) => {
	return (
		<select defaultValue={''} name="recurrency" onChange={onChange} className="form-input frequency" required>
			<option value="" disabled>
				Select Frequency
			</option>

			{frequencyOptions.map((option) => {
				return (
					<option key={option.value} value={option.value}>
						{option.label}
					</option>
				);
			})}
		</select>
	);
};

export default function ChecklistGenerator() {
	const [isLoading, setIsLoading] = useState(false);
	const [isLoadingBtn, setIsLoadingBtn] = useState(false);
	const [isTyping, setIsTyping] = useState(false);
	const [isCheckingOut, setIsCheckingOut] = useState(false);

	const [selectedFrequency, setSelectedFrequency] = useState();
	const [inputAssetType, setInputAssetType] = useState();
	const [generatedResult, setGeneratedResult] = useState({});
	const [list, setList] = useState('');
	const [checklistData, setChecklistData] = useState();
	const [combineData, setCombineData] = useState();

	useEffect(() => {
		const lastedData = { ...generatedResult, list };
		setCombineData(lastedData);
	}, [generatedResult]);

	const handleSubmit = async (e) => {
		setGeneratedResult({});
		setIsLoading(true);
		setIsTyping(true);

		try {
			e.preventDefault();

			const payload = {
				template_id: AI_SERVICE_PROMPTS.GENERATOR,
				prompt: `${selectedFrequency} checklist for ${inputAssetType}`,
			};
			const generateResponse = await axios.post('/api/ai-service/prompts/generate-prompt', payload);
			const checklistsGenerationResponse = new ChecklistsGenerationResponse(generateResponse);
			setGeneratedResult(checklistsGenerationResponse.result);
			setList(checklistsGenerationResponse.checklists);
			setIsLoading(false);
		} catch (error) {
			openNotification('error', 'Error', 'Something went wrong. Please try again!');
			setIsLoading(false);
		}
	};

	const handleAddMoreChecklists = async () => {
		setIsLoadingBtn(true);

		try {
			const payload = {
				template_id: AI_SERVICE_PROMPTS.ADD_MORE_CHECKS,
				prompt: JSON.stringify(generatedResult),
			};
			const generateMoreResponse = await axios.post('/api/ai-service/prompts/generate-prompt', payload);
			const moreChecklistsResponse = new ChecklistsGenerationResponse(generateMoreResponse);

			let result = moreChecklistsResponse.result;
			setList((prevList) => [...prevList, ...result]);
			setIsLoadingBtn(false);
		} catch (error) {
			openNotification('error', 'Error', 'Something went wrong. Please try again!');
			setIsLoadingBtn(false);
		}
	};

	const convertChecklistToString = (jsonChecklist, title) => {
		let textChecklist = '';
		for (let i = 0; i < jsonChecklist.length; i++) {
			textChecklist += `${i + 1}. ${jsonChecklist[i]}\n`;
		}
		return `${title ? `${title}\n\n` : ''}${generatedResult.description}\n\n${textChecklist}`;
	};

	const removeItem = (index) => {
		const updatedList = [...list];
		updatedList.splice(index, 1);
		setList(updatedList);
	};

	const handleDownloadPdfBtn = async () => {
		setIsCheckingOut(true);
		const payload = payloadToExportPdf(generatedResult.title, generatedResult.description, list);

		try {
			const response = await fileService.post('pdf/checklist', payload);
			downloadFile(response.data);
			setIsCheckingOut(false);
		} catch (error) {
			openNotification('error', 'Error', 'Something went wrong. Please try again!');
			setIsCheckingOut(false);
		}
	};

	const CopyButton = () => {
		const [isCopied, handleCopy] = useCopyToClipboard();
		const textToCopy = convertChecklistToString(list, generatedResult.title);
		return isCopied ? (
			<Tooltip title="Copied to clipboard">
				<i className="fas fa-clipboard-check first-line-icon hover" onClick={() => handleCopy(textToCopy)} />
			</Tooltip>
		) : (
			<Tooltip title="Copy to clipboard">
				<i className="fas fa-clipboard-list first-line-icon hover" onClick={() => handleCopy(textToCopy)} />
			</Tooltip>
		);
	};

	const handleBackToResult = () => {
		setChecklistData();
	};

	return (
		<Layout pageName="Automated Checklist Generator">
			<div className="resource-page resource-page--checklist-generator">
				<section className="heading">
					<div className="container">
						<h1 className="heading__title">Automated Checklist Generator</h1>
						<div className="heading__content">
							<h2 className="heading__desc">Automatically create checklists using ChatGPT technology</h2>
						</div>
					</div>
				</section>

				<section className="checklist-generator">
					<div className="container">
						<div className="checklist-generator-wrapper">
							<h2 className="title">
								Simply select the frequency and asset type and a checklist will be automatically generated
							</h2>

							<form className="form" onSubmit={handleSubmit}>
								<SelectFrequency onChange={(e) => setSelectedFrequency(e.target.value)} />

								<input
									name="asset_type"
									onChange={(e) => setInputAssetType(e.target.value)}
									placeholder="Asset Type"
									className="form-input asset-type"
									required
								/>

								<button type="submit" className="btn">
									Generate
								</button>
							</form>
						</div>

						{!checklistData ? (
							<div className="checklist-generator-wrapper">
								<div className="first-line">
									<div className="title">
										<i className={`fas fa-magic first-line-icon ${isLoading && 'fa-spin'}`} />
										<h2>
											{!!generatedResult.title ? generatedResult.title : 'Your checklist will be displayed below!'}
										</h2>
									</div>
									<div className="buttons">
										{!!list && !isTyping && (
											<>
												<SignupForm
													btnText="Sign Up for 14-day Trial and use Checklist in FacilityBot"
													btnClass="btn btn--checklist"
													type={{ name: 'pdf' }}
													checklistData={combineData}
												/>

												<Tooltip title="Download PDF file">
													<i
														className="fas fa-file-pdf first-line-icon hover"
														onClick={() => handleDownloadPdfBtn()}
													></i>
												</Tooltip>
												<CopyButton />
											</>
										)}
									</div>
								</div>

								<Spin className="custom-spin" size="large" spinning={isCheckingOut}>
									<div className="checklist-body">
										{!!list && isTyping && (
											<TypeAnimation
												style={{ whiteSpace: 'pre-line' }}
												sequence={[
													convertChecklistToString(list),
													() => {
														setIsTyping(false);
													},
												]}
												speed={85}
											/>
										)}

										{!!generatedResult.description && !isTyping && (
											<Fragment>
												<div>{generatedResult.description}</div>
												<br />
											</Fragment>
										)}

										{!!list && !isTyping && (
											<div>
												{list.map((item, index) => (
													<div key={index}>
														<ul>
															<li className="checklist-body--row">
																<span>
																	{index + 1}. {item}
																</span>

																<Tooltip title={`Remove item ${index + 1}.`}>
																	<DeleteOutlined onClick={() => removeItem(index)} />
																</Tooltip>
															</li>
														</ul>
													</div>
												))}

												<Button
													className="additional-btn"
													type="primary"
													loading={isLoadingBtn}
													onClick={() => handleAddMoreChecklists()}
												>
													+ Add 5 more checks
												</Button>
											</div>
										)}
									</div>
								</Spin>
							</div>
						) : (
							<ChecklistPdfDetail checklistDetail={checklistData} handleBackToResult={handleBackToResult} />
						)}
					</div>
				</section>

				<CtaForm />
			</div>
		</Layout>
	);
}
